import { useNavigate } from 'react-router-dom';
import { RouteId, RouteUrlGetterParams, routeToUrl } from '../routes';
import { useMemo } from 'react';

type HistoryAction<R extends RouteId> = {
  (): void;
  href: string;
  replace(alterParams: Partial<RouteUrlGetterParams<R>>): void;
  onClick(e?: any): void;
};

/**
 * Creates an navigation action callback
 *
 * Example:
 * ```ts
 * const goHome = useHistoryAction('home', { appType });
 * // in component:
 * <Btn onClick={goHome} />
 * // imperative:
 * goHome(historyState);
 * ```
 *
 * @param route Route
 * @param params Route parameters
 * @param action History action – push or replace
 * @param defaultState History state
 */
export function useHistoryAction<R extends RouteId, S = any>(
  route: R | null,
  params?: RouteUrlGetterParams<R>,
  action?: 'push' | 'replace',
  state?: S,
): HistoryAction<R> {
  const navigate = useNavigate();

  const historyAction: any = useMemo(() => {
    if (!route) {
      return () => {};
    }
    
    let url: string;
    const getUrl = routeToUrl[route] as (params?: RouteUrlGetterParams<R>) => string;

    try {
      url = getUrl(params);
    } catch (err) {
      console.error(`Cannot get route to ${route}`, params);
      url = `#error: ${(err as Error).message}`;
    }

    const historyAction = (() => {
      if (process.env.NODE_ENV !== 'production') {
        console.log(`History ${action}: ${route} => ${url}`);
      }
      navigate(url, { state, replace: action === 'replace' });
    }) as HistoryAction<R>;

    historyAction.href = url;

    historyAction.replace = (alterParams) => {
      const newUrl = getUrl({ ...((params as any) ?? {}), ...alterParams });
      if (process.env.NODE_ENV !== 'production') {
        console.log(`Navigation.replace: ${route} => ${newUrl}`);
      }
      navigate(newUrl, { state, replace: true });
    };

    historyAction.onClick = (e) => {
      e?.preventDefault?.();
      historyAction();
    };

    return historyAction;
  }, [action, navigate, params, route, state]);

  return historyAction;
}
